import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import NotFound from 'components/atoms/NotFound'
import PageLoading from 'components/atoms/PageLoading'
import PageSessionSelection from 'components/atoms/PageSessionSelection'
import PageTitle from 'components/atoms/PageTitle'
import Stepper from 'components/atoms/Stepper'
import {
  SESSIONS_STEPS_LIST,
  SESSIONS_STEP_BLIND_CAPS,
  SESSIONS_STEP_CAPS,
  SESSIONS_STEP_CONFIRMATION,
  SESSIONS_STEP_DISABLED,
  checkAssignment,
  getLastSessionState,
  getSessionsStepUrl,
} from 'components/organisms/Sessions/sessionsLib'
import useSessionStepNavigation from 'components/organisms/Sessions/useSessionStepNavigation'
import useSessionSteps from 'components/organisms/Sessions/useSessionSteps'
import SessionsCaps from 'components/organisms/SessionsCaps'
import SessionsCars from 'components/organisms/SessionsCars'
import SessionsConfirmation from 'components/organisms/SessionsConfirmation'
import SessionsPlanning from 'components/organisms/SessionsPlanning'
import SessionsPricing from 'components/organisms/SessionsPricing'
import SessionsSold from 'components/organisms/SessionsSold'

import useSession from 'hooks/useSession'
import useSessionsList from 'hooks/useSessionsList'

import {
  ROLES_ARVAL,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  useAuth,
} from '../../auth'

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function SessionsStepsRouter() {
  const { t } = useTranslation()

  const { id, step } = useParams()
  const [showHeader, setShowHeader] = useState(true)

  const {
    isFetching: isFetchingSessions,
    fetchSessions,
    sessions,
  } = useSessionsList()

  const {
    isFetching: isFetchingSession,
    isUpdating: isUpdatingSession,
    fetchSession,
    ingestCars,
    ingestPrices,
    ingestSold,
    updateDate,
    updateAllDates,
    updateCars,
    updatePrices,
    updateSold,
    updateCap,
    updateBlindCap,
    updatePlatePricing,
    updateShowOtherPartners,
    updateCanReprice,
    confirmStep,
    confirmBlindCaps,
    confirmNoSoldCars,
    session,
    error,
    syncAdditionalInfo,
  } = useSession()

  const { fetchCurrentSession, session: currentActiveSession } = useSession()

  const {
    carsStepCars,
    pricingStepCars,
    soldStepCars,
    initSteps,
    handleStepValidation,
    getStepsWithState,
  } = useSessionSteps()

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  useEffect(() => {
    if (!sessions.length) {
      return
    }
    fetchSession(id)
  }, [fetchSession, updateDate, sessions, id])

  useEffect(() => {
    if (!sessions.length) {
      return
    }
    fetchCurrentSession()
  }, [fetchCurrentSession, sessions])

  useEffect(() => {
    if (!session || !Object.keys(session).length) {
      return
    }
    initSteps(session)
  }, [initSteps, session])

  const assignment = useMemo(() => checkAssignment(session), [session])

  const steps = useMemo(() => getStepsWithState(), [getStepsWithState])

  const { goToStep } = useSessionStepNavigation()

  const handleStep = (index) => {
    goToStep(session.sessionId, index)
  }

  const handleConfirm = async () => {
    if (step === SESSIONS_STEP_CAPS) {
      const shouldConfirmBlindCaps =
        !session?.blindCapsAcceptedAt && !!session?.blindCaps?.length

      if (shouldConfirmBlindCaps) {
        const success = await confirmBlindCaps(session.sessionId)
        if (!success) return
      }

      await confirmStep(session.sessionId, step)
      return
    }

    await confirmStep(session.sessionId, step)
  }

  const { hasOneOfRoles } = useAuth()
  const canAccess = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () =>
      hasOneOfRoles([
        ROLE_READ_ONLY,
        ROLE_LOGISTICS,
        ROLE_B2C,
        ROLE_POST_SALES,
      ]),
    [hasOneOfRoles]
  )

  if (!canAccess) {
    return <Navigate to="/agenda" />
  }

  return (
    <div className={classnames('content-container', { details: !showHeader })}>
      {error && Number(error.response.status) === 404 ? (
        <NotFound />
      ) : isFetchingSessions ||
        isFetchingSession ||
        !Object.keys(session).length ||
        !Object.keys(steps).length ? (
        <PageLoading />
      ) : step === SESSIONS_STEP_BLIND_CAPS ? (
        <Navigate to={`/sessions/${session.sessionId}/${SESSIONS_STEP_CAPS}`} />
      ) : !step ||
        !SESSIONS_STEPS_LIST.includes(step) ||
        steps.find((s) => s.step === step).state === SESSIONS_STEP_DISABLED ? (
        <Navigate
          to={`/sessions/${session.sessionId}/${getLastSessionState(steps)}`}
        />
      ) : (
        <>
          {(showHeader || step !== SESSIONS_STEP_CONFIRMATION) && (
            <PageTitle
              label={t(`sessions.${step}.title`)}
              rightColumn={
                <PageSessionSelection
                  session={session}
                  currentActiveSession={currentActiveSession}
                  task="sessionCreation"
                  handleStepUrl={getSessionsStepUrl}
                  sessionsList={sessions}
                  readOnly={isUpdatingSession}
                />
              }
            />
          )}
          {step === SESSIONS_STEPS_LIST[0] ? (
            <>
              <Stepper steps={steps} activeStep={0} handleStep={handleStep} />
              <SessionsPlanning
                data-testid="session-planning"
                id={id}
                session={session}
                updateDate={updateDate}
                handleValidation={(sessionState) =>
                  handleStepValidation(step, sessionState)
                }
                handleConfirm={handleConfirm}
                handleBeforeAssignmentConfirm={updateAllDates}
                handleShowOtherPartners={updateShowOtherPartners}
                handleCanReprice={updateCanReprice}
                assignment={assignment}
                readOnly={isUpdatingSession}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === SESSIONS_STEPS_LIST[1] ? (
            <>
              <Stepper steps={steps} activeStep={1} handleStep={handleStep} />
              <SessionsCars
                id={id}
                data-testid="session-cars"
                session={session}
                stepCars={carsStepCars}
                updateCars={updateCars}
                handleIngestCars={ingestCars}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === SESSIONS_STEPS_LIST[2] ? (
            <>
              <Stepper steps={steps} activeStep={2} handleStep={handleStep} />
              <SessionsPricing
                id={id}
                data-testid="session-pricing"
                session={session}
                stepCars={pricingStepCars}
                updatePrices={updatePrices}
                handleIngestPrices={ingestPrices}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === SESSIONS_STEPS_LIST[3] ? (
            <>
              <Stepper steps={steps} activeStep={3} handleStep={handleStep} />
              <SessionsSold
                id={id}
                data-testid="session-sold"
                session={session}
                stepCars={soldStepCars}
                updateSold={updateSold}
                handleIngestSold={ingestSold}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                confirmNoSoldCars={confirmNoSoldCars}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === SESSIONS_STEPS_LIST[4] ? (
            <>
              <Stepper steps={steps} activeStep={4} handleStep={handleStep} />
              <SessionsCaps
                id={id}
                data-testid="session-caps"
                session={session}
                updateCap={updateCap}
                updateBlindCap={updateBlindCap}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : (
            <>
              {showHeader && (
                <Stepper steps={steps} activeStep={5} handleStep={handleStep} />
              )}
              <SessionsConfirmation
                id={id}
                data-testid="session-confirmation"
                session={session}
                updateDate={updateDate}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                updatePlatePricing={updatePlatePricing}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
                setShowHeader={setShowHeader}
                handleAdditionalInfoSync={syncAdditionalInfo}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
